'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { OverlaySliderHeroProps } from '@/components/hero/types';
import { Link } from '@/components/link/Link';
import Card from '@/components/card/Card';
import { BaseImage } from '@/components/baseImage/BaseImage';
import { Button } from '@/components/button/Button';
import { Headline } from '@/components/headline/Headline';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';
import '.././Hero.css';

export default function OverlaySliderHero({
  headline,
  description,
  link,
  image,
  cards,
  sliderLeft,
  sliderRight,
  spacingTop,
  spacingBottom,
  sectionId,
}: OverlaySliderHeroProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  // Use a ref to store the timer ID. The type will work in both browser/Node environments.
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleNext = useCallback(() => {
    if (cards) {
      setCurrentIndex((prevIndex) =>
        prevIndex < cards.length - 1 ? prevIndex + 1 : 0,
      );
    }
  }, [cards]);

  // This function resets the auto-advance timer.
  const resetAutoAdvance = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      handleNext();
    }, 3000);
  }, [handleNext]);

  const handlePrevious = () => {
    if (cards) {
      setCurrentIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : cards.length - 1,
      );
      resetAutoAdvance(); // Reset timer on manual click
    }
  };

  // On component mount (or when cards change), start the auto-advance timer.
  useEffect(() => {
    resetAutoAdvance();
    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
      }
    };
  }, [resetAutoAdvance]);

  // When the user manually clicks "Next", reset the auto-advance timer.
  const handleNextManual = () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
    }
    handleNext();
    resetAutoAdvance();
  };

  return (
    <div
      id={sectionId}
      className={`relative h-[91vh] w-full overflow-hidden md:h-screen lg:h-[85vh] ${
        spacingTop ? 'pt-4 md:pt-8 lg:pt-20' : ''
      } ${spacingBottom ? 'pb-4 md:pb-8 lg:pb-20' : ''}`}
    >
      <div className="container absolute left-1/2 z-10 grid h-auto -translate-x-1/2 transform grid-cols-4 gap-1 p-4 sm:grid-cols-8 sm:p-0 md:grid-cols-8 lg:bottom-0 lg:grid-cols-12 xl:grid-cols-12">
        <div className="col-span-4 mt-auto flex flex-col justify-start py-3 sm:col-span-6 md:col-span-6 md:py-6 lg:col-start-1 lg:col-end-8">
          <Headline
            {...headline}
            className="mb-6 text-4xl font-bold text-white md:text-5xl"
          >
            {headline.title}
          </Headline>
          {description && (
            <div className="content-dark mb-6 text-lg text-white sm:text-xl">
              <SerializeDescription description={description} />
            </div>
          )}
          {link && (
            <div className="split-image-hero__link w-fit">
              <Link {...link} className="px-0" />
            </div>
          )}
        </div>

        {cards && cards.length > 0 && (
          <div className="col-span-full">
            <div className="my-6 w-full border-b border-neutral-100 opacity-25 md:my-12 lg:my-0"></div>
          </div>
        )}

        {cards && cards.length > 0 && (
          <div className="mac-screen media-query-col col-span-4 flex flex-col items-start justify-between sm:col-span-12 sm:justify-center md:col-span-12 md:flex-row md:justify-center lg:col-start-6 lg:col-end-13 lg:row-start-12 lg:row-end-13 lg:pb-24 xl:col-start-9 xl:col-end-13">
            <div className="flex w-full overflow-hidden md:w-5/6 lg:w-5/6">
              <div
                className="flex h-full max-h-[13em] w-full transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {cards.map((card, index) => (
                  <div key={index} className="w-full flex-shrink-0">
                    <Card {...card} role="article" />
                  </div>
                ))}
              </div>
            </div>
            {/* Desktop slider buttons */}
            <div className="hidden items-center md:flex md:w-1/6 lg:flex lg:w-1/6">
              <Button
                aria-label={'Slide Left'}
                className="!ml-2 !p-1.5"
                onClick={handlePrevious}
                variant="secondary"
                sizes="small"
                leftIcon={{ name: 'chevron-left', size: '20px' }}
                role="button"
                {...sliderLeft}
              />
              <Button
                aria-label={'Slide Right'}
                className="!mx-2 !p-1.5"
                onClick={handleNextManual}
                variant="secondary"
                sizes="small"
                leftIcon={{ name: 'chevron-right', size: '20px' }}
                role="button"
                {...sliderRight}
              />
            </div>
            {/* Mobile slider buttons */}
            <div className="mt-4 flex w-full items-center justify-end md:hidden lg:hidden">
              <Button
                aria-label={'Slide Left'}
                className="!ml-2 !p-1.5"
                onClick={handlePrevious}
                variant="secondary"
                sizes="small"
                leftIcon={{ name: 'chevron-left', size: '20px' }}
                role="button"
                {...sliderLeft}
              />
              <Button
                aria-label={'Slide Right'}
                className="!ml-2 !p-1.5"
                onClick={handleNextManual}
                variant="secondary"
                sizes="small"
                leftIcon={{ name: 'chevron-right', size: '20px' }}
                role="button"
                {...sliderRight}
              />
            </div>
          </div>
        )}
      </div>
      {image && (
        <div className="h-screen">
          <BaseImage
            {...image}
            classNameImage="w-full h-screen"
            className="h-full w-full"
            prerender={true}
            objectFit="cover"
            sizes="100vw"
          />
          <div
            className="z-1 absolute inset-0 h-screen bg-neutral-700 bg-opacity-70"
            role="presentation"
          ></div>
        </div>
      )}
    </div>
  );
}
